/**
 * Email Processor Service
 * Handles email content extraction and processing for Outlook
 */

class EmailProcessor {
  constructor() {
    this.maxContentLength = 10000; // Maximum characters to process
  }

  /**
   * Automatically detect and get current email content (FAST VERSION)
   * @returns {Promise<Object>} Current email content and metadata
   */
  async getCurrentEmail() {
    try {
      // Quick email content extraction without complex processing
      const emailData = await this.getQuickEmailContent();
      // Add mode detection
      emailData.mode = this.detectEmailMode();
      return emailData;
    } catch (error) {
      console.error('Error getting email:', error);
      throw new Error('Failed to retrieve email content');
    }
  }

  /**
   * Detect if user is in compose or read mode
   * @returns {string} 'compose' or 'read'
   */
  detectEmailMode() {
    try {
      // Check if this is a new item being composed
      const item = Office.context.mailbox.item;
      
      // If item has no dateTimeCreated, it's likely being composed
      if (!item.dateTimeCreated) {
        return 'compose';
      }
      
      // Check if item is in draft state
      if (item.isDraft) {
        return 'compose';
      }
      
      // Check item type and class
      if (item.itemType === Office.MailboxEnums.ItemType.Message && 
          item.itemClass === 'IPM.Note' && 
          !item.dateTimeCreated) {
        return 'compose';
      }
      
      return 'read';
    } catch (error) {
      console.warn('Could not detect email mode, defaulting to read:', error);
      return 'read';
    }
  }

  /**
   * Insert generated email content into compose window
   * @param {string} emailContent - The generated email content to insert
   * @param {boolean} replaceAll - Whether to replace all content or append
   * @returns {Promise<boolean>} Success status
   */
  async insertEmailContent(emailContent, replaceAll = true) {
    return new Promise((resolve, reject) => {
      try {
        const item = Office.context.mailbox.item;
        
        if (replaceAll) {
          // Replace entire body content
          item.body.setAsync(
            emailContent,
            { coercionType: Office.CoercionType.Html },
            (result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                console.log('Email content inserted successfully');
                resolve(true);
              } else {
                console.error('Failed to insert email content:', result.error);
                reject(new Error(result.error.message));
              }
            }
          );
        } else {
          // Append to existing content
          item.body.setSelectedDataAsync(
            emailContent,
            { coercionType: Office.CoercionType.Html },
            (result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                console.log('Email content appended successfully');
                resolve(true);
              } else {
                console.error('Failed to append email content:', result.error);
                reject(new Error(result.error.message));
              }
            }
          );
        }
      } catch (error) {
        console.error('Error inserting email content:', error);
        reject(error);
      }
    });
  }

  /**
   * Fast email content extraction
   */
  async getQuickEmailContent() {
    return new Promise((resolve, reject) => {
      // Get just the essential content quickly
      Office.context.mailbox.item.body.getAsync(
        Office.CoercionType.Text,
        { asyncContext: 'quickGet' },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            const emailData = {
              content: this.cleanEmailContent(result.value),
              subject: Office.context.mailbox.item.subject || 'No Subject',
              timestamp: new Date().toISOString()
            };
            resolve(emailData);
          } else {
            reject(new Error(result.error.message || 'Failed to get email content'));
          }
        }
      );
    });
  }

  /**
   * Clean and optimize email content for AI processing
   */
  cleanEmailContent(content) {
    if (!content) return '';
    
    // Quick cleanup for better AI processing
    return content
      .replace(/\r\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim()
      .substring(0, this.maxContentLength);
  }

  /**
   * Get content from email being read
   */
  async getReadEmailContent() {
    return new Promise((resolve, reject) => {
      Office.context.mailbox.item.body.getAsync(
        Office.CoercionType.Text,
        { asyncContext: 'getBody' },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            const emailData = {
              subject: Office.context.mailbox.item.subject,
              body: result.value,
              sender: this.getSenderInfo(),
              recipients: this.getRecipientsInfo(),
              dateTime: Office.context.mailbox.item.dateTimeCreated,
              itemType: 'read',
              conversationId: Office.context.mailbox.item.conversationId
            };
            resolve(this.processEmailData(emailData));
          } else {
            reject(new Error(result.error.message));
          }
        }
      );
    });
  }

  /**
   * Get content from email being composed
   */
  async getComposeEmailContent() {
    return new Promise((resolve, reject) => {
      Office.context.mailbox.item.body.getAsync(
        Office.CoercionType.Text,
        { asyncContext: 'getBody' },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            const emailData = {
              subject: Office.context.mailbox.item.subject,
              body: result.value,
              recipients: this.getComposeRecipientsInfo(),
              itemType: 'compose',
              conversationId: Office.context.mailbox.item.conversationId
            };
            resolve(this.processEmailData(emailData));
          } else {
            reject(new Error(result.error.message));
          }
        }
      );
    });
  }

  /**
   * Get sender information for read emails
   */
  getSenderInfo() {
    try {
      const sender = Office.context.mailbox.item.sender || Office.context.mailbox.item.from;
      return {
        name: sender?.displayName || 'Unknown',
        email: sender?.emailAddress || 'unknown@example.com'
      };
    } catch (error) {
      return { name: 'Unknown', email: 'unknown@example.com' };
    }
  }

  /**
   * Get recipients information for read emails
   */
  getRecipientsInfo() {
    try {
      const recipients = {
        to: this.extractEmailAddresses(Office.context.mailbox.item.to),
        cc: this.extractEmailAddresses(Office.context.mailbox.item.cc),
        bcc: this.extractEmailAddresses(Office.context.mailbox.item.bcc)
      };
      return recipients;
    } catch (error) {
      return { to: [], cc: [], bcc: [] };
    }
  }

  /**
   * Get recipients information for compose emails
   */
  getComposeRecipientsInfo() {
    // For compose mode, we'll need to use async methods
    return new Promise((resolve) => {
      try {
        Office.context.mailbox.item.to.getAsync((result) => {
          const recipients = {
            to: result.status === Office.AsyncResultStatus.Succeeded ? result.value : [],
            cc: [],
            bcc: []
          };
          resolve(recipients);
        });
      } catch (error) {
        resolve({ to: [], cc: [], bcc: [] });
      }
    });
  }

  /**
   * Extract email addresses from recipient collection
   */
  extractEmailAddresses(recipients) {
    if (!recipients) return [];
    return recipients.map(recipient => ({
      name: recipient.displayName || recipient.name || '',
      email: recipient.emailAddress || recipient.address || ''
    }));
  }

  /**
   * Process and clean email data
   */
  processEmailData(emailData) {
    return {
      ...emailData,
      body: this.sanitizeContent(emailData.body),
      cleanBody: this.extractMainContent(emailData.body),
      wordCount: this.getWordCount(emailData.body),
      estimatedReadTime: this.estimateReadTime(emailData.body),
      hasAttachments: this.checkForAttachments(),
      importance: this.getImportance(),
      isReply: this.isReplyEmail(emailData.subject),
      isForward: this.isForwardEmail(emailData.subject)
    };
  }

  /**
   * Sanitize email content
   */
  sanitizeContent(content) {
    if (!content) return '';
    
    // Remove HTML tags if present
    let cleaned = content.replace(/<[^>]*>/g, '');
    
    // Remove excessive whitespace
    cleaned = cleaned.replace(/\s+/g, ' ').trim();
    
    // Limit content length
    if (cleaned.length > this.maxContentLength) {
      cleaned = cleaned.substring(0, this.maxContentLength) + '...';
    }
    
    return cleaned;
  }

  /**
   * Extract main content (remove signatures, headers, etc.)
   */
  extractMainContent(content) {
    if (!content) return '';
    
    let mainContent = content;
    
    // Remove common signature patterns
    const signaturePatterns = [
      /\n\s*--\s*\n[\s\S]*/,  // Standard signature delimiter
      /\n\s*Best regards[\s\S]*/i,
      /\n\s*Sincerely[\s\S]*/i,
      /\n\s*Thanks[\s\S]*\n[A-Za-z\s]+$/i,
      /\n\s*Sent from my[\s\S]*/i
    ];
    
    signaturePatterns.forEach(pattern => {
      mainContent = mainContent.replace(pattern, '');
    });
    
    // Remove quoted text (replies)
    const quotedPatterns = [
      /\n\s*>[\s\S]*/,  // Standard quoted text
      /\n\s*On .* wrote:[\s\S]*/,  // Outlook quoted text
      /\n\s*From:[\s\S]*/  // Forward headers
    ];
    
    quotedPatterns.forEach(pattern => {
      mainContent = mainContent.replace(pattern, '');
    });
    
    return mainContent.trim();
  }

  /**
   * Get word count
   */
  getWordCount(content) {
    if (!content) return 0;
    return content.trim().split(/\s+/).filter(word => word.length > 0).length;
  }

  /**
   * Estimate reading time (average 200 words per minute)
   */
  estimateReadTime(content) {
    const wordCount = this.getWordCount(content);
    return Math.max(1, Math.ceil(wordCount / 200));
  }

  /**
   * Check for attachments
   */
  checkForAttachments() {
    try {
      return Office.context.mailbox.item.attachments && 
             Office.context.mailbox.item.attachments.length > 0;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get email importance
   */
  getImportance() {
    try {
      return Office.context.mailbox.item.importance || 'normal';
    } catch (error) {
      return 'normal';
    }
  }

  /**
   * Check if email is a reply
   */
  isReplyEmail(subject) {
    if (!subject) return false;
    return /^(re:|fw:|fwd:)/i.test(subject.trim());
  }

  /**
   * Check if email is a forward
   */
  isForwardEmail(subject) {
    if (!subject) return false;
    return /^(fw:|fwd:)/i.test(subject.trim());
  }

  /**
   * Set email body content (for compose mode)
   */
  async setEmailBody(content, coercionType = Office.CoercionType.Text) {
    return new Promise((resolve, reject) => {
      Office.context.mailbox.item.body.setAsync(
        content,
        { coercionType },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(true);
          } else {
            reject(new Error(result.error.message));
          }
        }
      );
    });
  }

  /**
   * Insert content at cursor position
   */
  async insertContentAtCursor(content) {
    return new Promise((resolve, reject) => {
      Office.context.mailbox.item.body.setSelectedDataAsync(
        content,
        { coercionType: Office.CoercionType.Text },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(true);
          } else {
            reject(new Error(result.error.message));
          }
        }
      );
    });
  }

  /**
   * Get selected text from email body
   */
  async getSelectedText() {
    return new Promise((resolve, reject) => {
      Office.context.mailbox.item.body.getSelectedDataAsync(
        Office.CoercionType.Text,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value);
          } else {
            reject(new Error(result.error.message));
          }
        }
      );
    });
  }

  /**
   * Replace email content with new content
   * @param {string} newContent - New content to replace with
   * @param {boolean} replaceEntireBody - Whether to replace entire body or just selected text
   */
  async replaceEmailContent(newContent, replaceEntireBody = false) {
    return new Promise((resolve, reject) => {
      try {
        if (replaceEntireBody) {
          // Replace entire email body
          Office.context.mailbox.item.body.setAsync(
            newContent,
            { coercionType: Office.CoercionType.Text },
            (result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                resolve({ success: true, message: 'Email content replaced successfully' });
              } else {
                reject(new Error(result.error.message));
              }
            }
          );
        } else {
          // Replace selected text or insert at cursor
          Office.context.mailbox.item.body.setSelectedDataAsync(
            newContent,
            { coercionType: Office.CoercionType.Text },
            (result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                resolve({ success: true, message: 'Selected content replaced successfully' });
              } else {
                reject(new Error(result.error.message));
              }
            }
          );
        }
      } catch (error) {
        reject(new Error(`Failed to replace email content: ${error.message}`));
      }
    });
  }

  /**
   * Insert content at cursor position
   * @param {string} content - Content to insert
   */
  async insertContent(content) {
    return new Promise((resolve, reject) => {
      Office.context.mailbox.item.body.setSelectedDataAsync(
        content,
        { coercionType: Office.CoercionType.Text },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve({ success: true, message: 'Content inserted successfully' });
          } else {
            reject(new Error(result.error.message));
          }
        }
      );
    });
  }

  /**
   * Get selected text from email
   * Note: In Outlook, we need to use a different approach since getSelectedDataAsync
   * is not available. We'll prompt the user to copy text or use the full body.
   */
  async getSelectedText() {
    return new Promise((resolve, reject) => {
      try {
        // First, try to check if we have access to document selection (may not work in Outlook)
        if (typeof window !== 'undefined' && window.getSelection) {
          const selection = window.getSelection();
          if (selection && selection.toString().trim()) {
            resolve(selection.toString().trim());
            return;
          }
        }
        
        // If no selection, check clipboard (requires user permission)
        if (navigator.clipboard && navigator.clipboard.readText) {
          navigator.clipboard.readText().then(text => {
            if (text && text.trim()) {
              resolve(text.trim());
            } else {
              reject(new Error("No text found in clipboard. Please copy the text you want to process."));
            }
          }).catch(() => {
            reject(new Error("Unable to access clipboard. Please copy the text you want to process and try again."));
          });
        } else {
          reject(new Error("Please copy the text you want to process to the clipboard and try again."));
        }
      } catch (error) {
        reject(new Error("Unable to get selected text. Please copy the text you want to process and try again."));
      }
    });
  }

  /**
   * Clean HTML content by removing tags and converting to plain text
   * @param {string} htmlContent - HTML content to clean
   * @returns {string} Plain text content
   */
  cleanHtmlContent(htmlContent) {
    if (!htmlContent || htmlContent === null || htmlContent === undefined) {
      return '';
    }

    // Convert HTML to plain text
    let text = htmlContent
      // Remove script and style tags completely
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
      // Convert br tags to newlines
      .replace(/<br\s*\/?>/gi, '\n')
      // Convert p tags to double newlines
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<p[^>]*>/gi, '')
      // Remove all other HTML tags
      .replace(/<[^>]*>/g, '')
      // Decode HTML entities
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ')
      // Clean up multiple newlines
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    return text;
  }

  /**
   * Check if the current email is in compose mode
   * @returns {boolean} True if in compose mode, false if in read mode
   */
  isComposeMode() {
    try {
      const item = Office.context.mailbox.item;
      
      // Check if item has recipients being set (indicates compose mode)
      if (!item.to || item.to === undefined) {
        return true;
      }
      
      // Check if item is in draft state
      if (item.isDraft) {
        return true;
      }
      
      // Check if dateTimeCreated is missing (new item)
      if (!item.dateTimeCreated) {
        return true;
      }
      
      return false;
    } catch (error) {
      console.warn('Could not detect compose mode, defaulting to false:', error);
      return false;
    }
  }
}

export const emailProcessor = new EmailProcessor();
export default emailProcessor;